import { t } from '@lingui/core/macro';
import { useState } from 'react';

import LetteredAvatar from '@/components/LetteredAvatar';
import { cn } from '@/lib/utils';

const BRANDFETCH_CLIENT_ID = '1iddNS2YwMaoggrsXfJ';

type LogoSize = 'small' | 'medium' | 'large' | 'xlarge';

/** CSS display size → 2× request size for retina. */
const LOGO_PX: Record<LogoSize, number> = {
  small: 40,
  medium: 48,
  large: 64,
  xlarge: 80,
};

type Props = {
  className?: string;
  name?: string;
  domain?: string;
  size?: LogoSize;
};

const CompanyLogo = ({ className, name, domain, size = 'small' }: Props) => {
  const displayName = name ?? domain;
  const [failed, setFailed] = useState(false);

  if (domain == null || failed) {
    return <LetteredAvatar name={displayName} shape="square" size={size} />;
  }

  const px = LOGO_PX[size];
  const encodedDomain = encodeURIComponent(domain);
  const src = `https://cdn.brandfetch.io/domain/${encodedDomain}/fallback/404/w/${px}/h/${px}?c=${BRANDFETCH_CLIENT_ID}`;

  return (
    <img
      className={cn(
        'my-auto',
        size === 'small' && 'max-h-5 max-w-5 rounded-sm',
        size === 'medium' && 'max-h-6 max-w-6 rounded-sm',
        size === 'large' && 'max-h-8 max-w-8 rounded-md',
        size === 'xlarge' && 'max-h-10 max-w-25 rounded-md',
        className,
      )}
      src={src}
      alt={displayName ? t`${displayName} logo` : t`Company logo`}
      loading="lazy"
      decoding="async"
      // Brandfetch rejects requests without a Referer (no-referrer breaks logos).
      referrerPolicy="strict-origin-when-cross-origin"
      onError={() => setFailed(true)}
    />
  );
};

export default CompanyLogo;
